import { useState, useEffect, useCallback } from 'react';
import {
  incentivesApi,
  TOOLS_OPTIONS,
} from '../api/incentivesApi';
import {
  DATE_PRESET_OPTIONS,
  getDatePresetRange,
} from '../../common/compensationDateUtils';
import { useExportJob } from '../../../../hooks/useExportJob';
import { appToast } from '../../../../components/AppToast';
import { appConfirm } from '../../../../components/AppConfirmModal';
import { executePrintReport } from '../../../../utils/printReportUtil';

const REPORT_COLUMNS = [
  { key: 'company_name', label: 'Company' },
  { key: 'branch_name', label: 'Branch' },
  { key: 'department_name', label: 'Department' },
  { key: 'emp_id', label: 'Emp ID' },
  { key: 'emp_name', label: 'Employee Name' },
  { key: 'vno', label: 'Voucher No' },
  { key: 'v_date_formatted', label: 'Date' },
  { key: 'pay_head_name', label: 'Pay Head' },
  { key: 'amount', label: 'Amount', align: 'right', format: 'currency' },
];

export function useIncentivesList() {
  // Legacy Filter States
  const [datePreset, setDatePresetState] = useState('All');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [fromEmpId, setFromEmpId] = useState('');
  const [tillEmpId, setTillEmpId] = useState('');
  const [fromAmount, setFromAmount] = useState('');
  const [tillAmount, setTillAmount] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [payHeadFilter, setPayHeadFilter] = useState('');

  // Data & Pagination
  const [records, setRecords] = useState([]);
  const [dynamicOptions, setDynamicOptions] = useState({
    employees: [],
    payHeads: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25); // Standard pagination fixed page size
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Background Export Job Integration
  const { triggerExport, isExporting } = useExportJob({
    module: 'emp_incentives',
    jobType: 'EXPORT',
    fileFormat: 'XLSX',
  });

  // Load Dropdown Options
  useEffect(() => {
    incentivesApi
      .getIncentiveOptions()
      .then((opts) => setDynamicOptions(opts))
      .catch((err) => console.error('Failed to load filter options:', err));
  }, []);

  // Update Date Preset handler
  const setDatePreset = (presetKey) => {
    setDatePresetState(presetKey);
    if (presetKey !== 'Custom') {
      const { fromDate: f, toDate: t } = getDatePresetRange(presetKey);
      setFromDate(f);
      setToDate(t);
    }
  };

  const handleFromDateChange = (val) => {
    setFromDate(val);
    setDatePresetState('Custom');
  };

  const handleToDateChange = (val) => {
    setToDate(val);
    setDatePresetState('Custom');
  };

  const getActiveFilterParams = useCallback(() => {
    return {
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      fromEmpId: fromEmpId ? Number(fromEmpId) : undefined,
      tillEmpId: tillEmpId ? Number(tillEmpId) : undefined,
      fromAmount: fromAmount ? Number(fromAmount) : undefined,
      tillAmount: tillAmount ? Number(tillAmount) : undefined,
      employee: employeeFilter.trim() || undefined,
      payHeadId: payHeadFilter ? Number(payHeadFilter) : undefined,
    };
  }, [fromDate, toDate, fromEmpId, tillEmpId, fromAmount, tillAmount, employeeFilter, payHeadFilter]);

  const loadRecords = useCallback(async () => {
    setIsLoading(true);
    try {
      const filters = getActiveFilterParams();
      const data = await incentivesApi.getIncentives({
        ...filters,
        page,
        pageSize,
      });
      setRecords(data.items || []);
      setTotalCount(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error('Failed to load incentives:', err);
      appToast.error('Failed to load Incentives.');
    } finally {
      setIsLoading(false);
    }
  }, [getActiveFilterParams, page, pageSize]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const handleSearch = () => {
    setPage(1);
    loadRecords();
  };

  const resetFilters = () => {
    setDatePresetState('All');
    setFromDate('');
    setToDate('');
    setFromEmpId('');
    setTillEmpId('');
    setFromAmount('');
    setTillAmount('');
    setEmployeeFilter('');
    setPayHeadFilter('');
    setPage(1);
  };

  const handleDelete = async (id, employee) => {
    const confirmed = await appConfirm({
      title: 'Delete Incentive Voucher',
      message: `Are you sure you want to delete the incentive record for ${employee || 'this employee'}? This action cannot be undone.`,
      confirmLabel: 'Delete Voucher',
      cancelLabel: 'Cancel',
      variant: 'danger',
    });

    if (!confirmed) return;

    try {
      await incentivesApi.deleteIncentive(id);
      appToast.success('Incentive record deleted successfully.');
      loadRecords();
    } catch (err) {
      console.error('Delete error:', err);
      appToast.error('Failed to delete incentive.');
    }
  };

  const handleToolSelect = async (toolKey) => {
    const activeFilters = getActiveFilterParams();

    if (toolKey === 'print') {
      executePrintReport({
        title: 'Employee Incentives Report',
        columns: REPORT_COLUMNS,
        metadata: {
          'Date Range': fromDate && toDate ? `${fromDate} to ${toDate}` : (datePreset || 'All'),
          'Employee': employeeFilter || 'All',
        },
        fetchReportData: async () => {
          const res = await incentivesApi.getIncentivesReport(activeFilters);
          return { items: res?.items || [], total_amount: res?.total_amount || 0 };
        },
        onExportPdf: () => triggerExport(activeFilters, 'PDF'),
        onExportExcel: () => triggerExport(activeFilters, 'XLSX'),
        threshold: 1000,
      });
    } else if (toolKey === 'export_excel') {
      triggerExport(activeFilters, 'XLSX');
    } else if (toolKey === 'export_pdf') {
      triggerExport(activeFilters, 'PDF');
    }
  };

  return {
    state: {
      records,
      isLoading,
      datePreset,
      datePresetOptions: DATE_PRESET_OPTIONS,
      fromDate,
      toDate,
      fromEmpId,
      tillEmpId,
      fromAmount,
      tillAmount,
      employeeFilter,
      payHeadFilter,
      page,
      pageSize,
      totalCount,
      totalPages,
      tools: TOOLS_OPTIONS,
      dynamicOptions,
      isExporting,
    },
    actions: {
      setDatePreset,
      setFromDate: handleFromDateChange,
      setToDate: handleToDateChange,
      setFromEmpId,
      setTillEmpId,
      setFromAmount,
      setTillAmount,
      setEmployeeFilter,
      setPayHeadFilter,
      setPage,
      handleSearch,
      resetFilters,
      handleDelete,
      handleToolSelect,
      refresh: loadRecords,
    },
  };
}
